// Every setting of an experiment: its look, its method (acuity or contrast), attention checks,
// timing and edge jitter. Each setting has hover help (components/Help, help/*).

import type { HelpContext } from "../help/types";
import { ACUITY_METHODS } from "../lib/summaries";
import {
  DEFAULT_APPEARANCE, DEFAULT_CONE_OBSERVER, DEFAULT_EDGE_JITTER, DEFAULT_TIMING, withStaircaseDefaults,
  type AcuityConfig, type ContrastConfig, type EdgeJitter, type ExperimentConfig, type RGB, type Timing,
} from "../lib/protocol";
import { ContrastMethodFields, NumberField } from "./ContrastMethod";
import { Help } from "./Help";
import { JitterEditor, RgbField } from "./LookFields";

function fullJitter(j: Partial<EdgeJitter> | undefined): EdgeJitter {
  return { ...DEFAULT_EDGE_JITTER, ...(j ?? {}), cone_observer: { ...DEFAULT_CONE_OBSERVER, ...(j?.cone_observer ?? {}) } };
}

/** Older settings miss newer fields: fill them so the editor has every one. */
export function completeExperiment(e: ExperimentConfig): ExperimentConfig {
  return {
    ...e, appearance: { ...DEFAULT_APPEARANCE, ...(e.appearance ?? {}) },
    acuity: e.acuity ? { ...e.acuity, timing: { ...DEFAULT_TIMING, ...e.acuity.timing }, edge_jitter: fullJitter(e.acuity.edge_jitter) } : null,
    contrast: e.contrast ? { ...withStaircaseDefaults(e.contrast), timing: { ...DEFAULT_TIMING, ...e.contrast.timing },
      edge_jitter: fullJitter(e.contrast.edge_jitter) } : null,
  };
}

function Choice<T extends string | number>({ label, help, ctx, value, options, onChange }: {
  label: string; help: string; ctx: HelpContext; value: T; options: Array<[T, string]>; onChange: (v: T) => void;
}) {
  return (
    <label><span>{label}<Help k={help} ctx={ctx} /></span>
      <select aria-label={label} value={String(value)}
        onChange={(e) => onChange(options.find(([k]) => String(k) === e.target.value)![0])}>
        {options.map(([k, l]) => <option key={String(k)} value={String(k)}>{l}</option>)}
      </select>
    </label>
  );
}

export function ExperimentFields({ value, onChange }: { value: ExperimentConfig; onChange: (v: ExperimentConfig) => void }) {
  const e = value;
  const set = (patch: Partial<ExperimentConfig>) => onChange({ ...e, ...patch });
  const ctx: HelpContext = { experiment: e, acuity: e.acuity ?? undefined, contrast: e.contrast ?? undefined };
  const Ap = e.appearance;
  const bg: RGB = Ap.background_rgb_rel ?? [e.background_rel, e.background_rel, e.background_rel];
  return (
    <>
      <h3>How it looks</h3>
      <div className="row">
        <RgbField label="Background" value={bg} onChange={(v) => set({ appearance: { ...Ap, background_rgb_rel: v } })} />
        <RgbField label="Letter" value={Ap.letter_rgb_rel} onChange={(v) => set({ appearance: { ...Ap, letter_rgb_rel: v } })} />
        <div><Help k="look.colours" ctx={ctx} /></div>
        <button className="secondary" onClick={() => set({ background_rel: 1, appearance: { ...DEFAULT_APPEARANCE } })}>Black on white</button>
      </div>
      {e.kind === "contrast" && (
        <fieldset>
          <legend>Contrast letters<Help k="look.contrast_mode" ctx={ctx} /></legend>
          <label style={{ flexDirection: "row", gap: "0.3rem" }}>
            <input type="radio" name="cmode" checked={Ap.contrast_mode === "luminance"} onChange={() => set({ appearance: { ...Ap, contrast_mode: "luminance" } })} />
            a darker shade of the background (score: Weber luminance contrast, log CS)</label>
          <label style={{ flexDirection: "row", gap: "0.3rem" }}>
            <input type="radio" name="cmode" checked={Ap.contrast_mode === "toward_letter"} onChange={() => set({ appearance: { ...Ap, contrast_mode: "toward_letter" } })} />
            fading from the background toward the letter colour (score: log modulation, labelled as such)</label>
        </fieldset>
      )}
      <p className="small muted">Colours are linear fractions of each primary (0–1; the swatch shows them in sRGB). Results with other
        colours, another contrast mode or edge jitter are kept as their own series in the history, never compared with black-on-white results.</p>

      {e.kind === "acuity" && e.acuity && <AcuityFields a={e.acuity} ctx={ctx} onChange={(a) => set({ acuity: a })} />}
      {e.kind === "contrast" && e.contrast && <ContrastFields c={e.contrast} ctx={ctx} onChange={(c) => set({ contrast: c })} />}

      <h3>Edge jitter (visualTools chromatic outline)<Help k="jitter.enabled" ctx={ctx} /></h3>
      {e.acuity && <JitterEditor title="Acuity test" value={e.acuity.edge_jitter} background={bg}
        onChange={(v) => set({ acuity: { ...e.acuity!, edge_jitter: v } })} />}
      {e.contrast && <JitterEditor title="Contrast test" value={e.contrast.edge_jitter} background={bg}
        onChange={(v) => set({ contrast: { ...e.contrast!, edge_jitter: v } })} />}
    </>
  );
}

function TimingFields({ t, ctx, onChange }: { t: Timing; ctx: HelpContext; onChange: (t: Timing) => void }) {
  return (
    <>
      <h3>Timing</h3>
      <div className="row">
        <NumberField help="timing.stimulus_ms" ctx={ctx} label="Letter shown for (ms, 0 = until answered)" value={t.stimulus_ms}
          min={0} max={10000} step={50} integer onCommit={(v) => onChange({ ...t, stimulus_ms: v })} />
        <NumberField help="timing.isi_ms" ctx={ctx} label="Blank between letters (ms)" value={t.isi_ms}
          min={0} max={5000} step={50} integer onCommit={(v) => onChange({ ...t, isi_ms: v })} />
        <NumberField help="timing.response_timeout_ms" ctx={ctx} label="Answer within (ms, 0 = no limit)" value={t.response_timeout_ms}
          min={0} max={60000} step={500} integer onCommit={(v) => onChange({ ...t, response_timeout_ms: v })} />
      </div>
    </>
  );
}

function CatchFields({ rate, minPct, action, ctx, prefix, onChange }: {
  rate: number; minPct: number; action: "flag" | "invalid"; ctx: HelpContext; prefix: "acuity" | "contrast";
  onChange: (p: { catch_rate?: number; catch_min_pct?: number; catch_action?: "flag" | "invalid" }) => void;
}) {
  return (
    <div className="row">
      <NumberField help={`${prefix}.catch_rate`} ctx={ctx} label="Attention checks (%)" value={rate} scale={100} min={0} max={30} step={1}
        onCommit={(v) => onChange({ catch_rate: v })} />
      {rate > 0 && (
        <>
          <NumberField help={`${prefix}.catch_min_pct`} ctx={ctx} label="Checks right at least (%)" value={minPct} min={0} max={100} step={5}
            onCommit={(v) => onChange({ catch_min_pct: v })} />
          <Choice label="If fewer" help={`${prefix}.catch_action`} ctx={ctx} value={action}
            options={[["flag", "flag the result"], ["invalid", "the result does not count"]]}
            onChange={(v) => onChange({ catch_action: v })} />
        </>
      )}
    </div>
  );
}

function AcuityFields({ a, ctx, onChange }: { a: AcuityConfig; ctx: HelpContext; onChange: (a: AcuityConfig) => void }) {
  const set = (patch: Partial<AcuityConfig>) => onChange({ ...a, ...patch });
  const quest = a.method === "quest";
  return (
    <>
      <h3>Acuity method</h3>
      <div className="row">
        <Choice label="Method" help="acuity.method" ctx={ctx} value={a.method}
          options={ACUITY_METHODS as Array<[AcuityConfig["method"], string]>} onChange={(v) => set({ method: v })} />
        <NumberField help="acuity.min_logmar" ctx={ctx} label="Smallest letter (logMAR)" value={a.min_logmar} min={-0.5} max={2.5} step={0.1}
          onCommit={(v) => set({ min_logmar: v })} />
        <NumberField help="acuity.max_logmar" ctx={ctx} label="Largest letter (logMAR)" value={a.max_logmar} min={-0.3} max={2.7} step={0.1}
          onCommit={(v) => set({ max_logmar: v })} />
      </div>
      {quest ? (
        <div className="row">
          <NumberField help="acuity.quest_trials" ctx={ctx} label="Trials per staircase (2 staircases)" value={a.quest_trials} min={5} max={80} step={1} integer
            onCommit={(v) => set({ quest_trials: v })} />
          <NumberField help="acuity.quest_prior_mean" ctx={ctx} label="Prior guess (logMAR)" value={a.quest_prior_mean} min={-0.3} max={2.5} step={0.1}
            onCommit={(v) => set({ quest_prior_mean: v })} />
          <NumberField help="acuity.quest_prior_sd" ctx={ctx} label="Prior spread (SD, logMAR)" value={a.quest_prior_sd} min={0.05} max={3} step={0.05}
            onCommit={(v) => set({ quest_prior_sd: v })} />
          <NumberField help="acuity.quest_beta" ctx={ctx} label="Slope β (per logMAR)" value={a.quest_beta} min={1} max={60} step={1}
            onCommit={(v) => set({ quest_beta: v })} />
          <NumberField help="acuity.quest_gamma" ctx={ctx} label="Guess rate γ" value={a.quest_gamma} min={0} max={0.5} step={0.05}
            onCommit={(v) => set({ quest_gamma: v })} />
          <NumberField help="acuity.quest_delta" ctx={ctx} label="Lapse rate δ" value={a.quest_delta} min={0} max={0.2} step={0.01}
            onCommit={(v) => set({ quest_delta: v })} />
          <NumberField help="acuity.quest_grain" ctx={ctx} label="Grid step (logMAR)" value={a.quest_grain} min={0.001} max={0.1} step={0.005}
            onCommit={(v) => set({ quest_grain: v })} />
          <NumberField help="acuity.quest_initial_easy" ctx={ctx} label="Easy trials first" value={a.quest_initial_easy} min={0} max={5} step={1} integer
            onCommit={(v) => set({ quest_initial_easy: v })} />
          <NumberField help="acuity.quest_descent_step" ctx={ctx} label="Descent step (logMAR)" value={a.quest_descent_step} min={0.05} max={1} step={0.05}
            onCommit={(v) => set({ quest_descent_step: v })} />
        </div>
      ) : (
        <div className="row">
          <NumberField help="acuity.start_low" ctx={ctx} label="Small start (logMAR)" value={a.start_low} min={-0.3} max={2.5} step={0.1}
            onCommit={(v) => set({ start_low: v })} />
          <NumberField help="acuity.start_high" ctx={ctx} label="Large start (logMAR)" value={a.start_high} min={-0.3} max={2.7} step={0.1}
            onCommit={(v) => set({ start_high: v })} />
          <NumberField help="acuity.step" ctx={ctx} label="First step (logMAR)" value={a.step} min={0.02} max={1} step={0.05}
            onCommit={(v) => set({ step: v })} />
          <NumberField help="acuity.step_factor" ctx={ctx} label="Step shrink factor" value={a.step_factor} min={0.1} max={1} step={0.05}
            onCommit={(v) => set({ step_factor: v })} />
          {a.method === "levitt" && (
            <Choice label="Right in a row to step down" help="acuity.levitt_n_down" ctx={ctx} value={a.levitt_n_down}
              options={[[2, "2 (70.7% correct)"], [3, "3 (79.4% correct)"], [4, "4 (84.1% correct)"]]}
              onChange={(v) => set({ levitt_n_down: v })} />
          )}
          {a.method === "weighted" && (
            <NumberField help="acuity.weighted_target" ctx={ctx} label="Target % correct" value={a.weighted_target} scale={100} min={30} max={95} step={5}
              onCommit={(v) => set({ weighted_target: v })} />
          )}
          <NumberField help="acuity.max_reversals" ctx={ctx} label="Stop after reversals" value={a.max_reversals} min={2} max={40} step={1} integer
            onCommit={(v) => set({ max_reversals: v })} />
          <NumberField help="acuity.reversals_to_average" ctx={ctx} label="Reversals averaged" value={a.reversals_to_average} min={1} max={40} step={1} integer
            onCommit={(v) => set({ reversals_to_average: v })} />
        </div>
      )}
      <CatchFields rate={a.catch_rate} minPct={a.catch_min_pct} action={a.catch_action} ctx={ctx} prefix="acuity"
        onChange={(p) => set(p)} />
      <p className="small muted">Attention checks in acuity are shown at the largest letter and feed no staircase.</p>
      <TimingFields t={a.timing} ctx={ctx} onChange={(t) => set({ timing: t })} />
    </>
  );
}

function ContrastFields({ c, ctx, onChange }: { c: ContrastConfig; ctx: HelpContext; onChange: (c: ContrastConfig) => void }) {
  const C = withStaircaseDefaults(c);
  const set = (patch: Partial<ContrastConfig>) => onChange({ ...C, ...patch });
  const questish = C.method === "quest" || (C.method === "staircases" && C.staircase_type === "quest");
  return (
    <>
      <h3>Letter size</h3>
      <div className="row">
        <fieldset>
          <legend>Contrast letter size<Help k="contrast.size_rule" ctx={ctx} /></legend>
          <label style={{ flexDirection: "row", gap: "0.3rem" }}>
            <input type="radio" name="size" checked={C.size_rule === "pelli_robson"} onChange={() => set({ size_rule: "pelli_robson" })} />
            Pelli-Robson chart size:
            <input type="number" step="0.1" min="0.5" max="10" style={{ width: "5rem" }} value={C.pelli_robson_letter_deg}
              aria-label="Letter size in degrees" onChange={(ev) => { const v = Number(ev.target.value); if (Number.isFinite(v) && v > 0) set({ pelli_robson_letter_deg: v }); }} />°
          </label>
          <label style={{ flexDirection: "row", gap: "0.3rem" }}>
            <input type="radio" name="size" checked={C.size_rule === "acuity_relative"} onChange={() => set({ size_rule: "acuity_relative" })} />
            Above this eye's acuity by
            <input type="number" step="0.05" min="0" max="1" style={{ width: "5rem" }} value={C.acuity_offset_logmar}
              aria-label="Offset above acuity" onChange={(ev) => { const v = Number(ev.target.value); if (Number.isFinite(v)) set({ acuity_offset_logmar: v }); }} /> logMAR
          </label>
          <div className="small muted">“Above acuity” needs an acuity experiment earlier in the program.</div>
        </fieldset>
        <NumberField help="contrast.gate_trials" ctx={ctx} label="Size check: Es at full contrast" value={C.gate_trials} min={1} max={10} step={1} integer
          onCommit={(v) => set({ gate_trials: v })} />
        <NumberField help="contrast.gate_step_logmar" ctx={ctx} label="If missed, letters grow by (logMAR)" value={C.gate_step_logmar} min={0.05} max={0.5} step={0.05}
          onCommit={(v) => set({ gate_step_logmar: v })} />
        <NumberField help="contrast.gate_max_attempts" ctx={ctx} label="Size check attempts" value={C.gate_max_attempts} min={1} max={10} step={1} integer
          onCommit={(v) => set({ gate_max_attempts: v })} />
      </div>
      <h3>Contrast method</h3>
      <div className="row">
        <ContrastMethodFields value={C} onChange={onChange} />
      </div>
      {C.method === "pelli_robson" && (
        <div className="row">
          <NumberField help="contrast.pr_step_log_cs" ctx={ctx} label="Step between levels (log CS)" value={C.pr_step_log_cs} min={0.05} max={0.5} step={0.05}
            onCommit={(v) => set({ pr_step_log_cs: v })} />
          <NumberField help="contrast.pr_max_log_cs" ctx={ctx} label="Faintest level (log CS)" value={C.pr_max_log_cs} min={0.5} max={3} step={0.15}
            onCommit={(v) => set({ pr_max_log_cs: v })} />
        </div>
      )}
      <details>
        <summary className="small">More staircase settings</summary>
        <div className="row">
          {C.method === "staircases" && C.staircase_type !== "quest" && (
            <NumberField help="contrast.discard_reversals" ctx={ctx} label="Early reversals left out" value={C.discard_reversals} min={0} max={6} step={1} integer
              onCommit={(v) => set({ discard_reversals: v })} />
          )}
          {C.method === "staircases" && (
            <NumberField help="contrast.staircase_max_log_cs" ctx={ctx} label="Faintest a staircase may ask (log CS)" value={C.staircase_max_log_cs} min={0.5} max={3.5} step={0.1}
              onCommit={(v) => set({ staircase_max_log_cs: v })} />
          )}
          {C.method === "quest" && (
            <NumberField help="contrast.quest_trials" ctx={ctx} label="QUEST pair: trials per staircase" value={C.quest_trials} min={5} max={80} step={1} integer
              onCommit={(v) => set({ quest_trials: v })} />
          )}
          {questish && (
            <>
              <NumberField help="contrast.quest_prior_mean" ctx={ctx} label="Prior guess (log10 Weber; −1.5 = log CS 1.5)" value={C.quest_prior_mean} min={-3} max={0} step={0.1}
                onCommit={(v) => set({ quest_prior_mean: v })} />
              <NumberField help="contrast.quest_prior_sd" ctx={ctx} label="Prior spread (SD, log units)" value={C.quest_prior_sd} min={0.05} max={3} step={0.05}
                onCommit={(v) => set({ quest_prior_sd: v })} />
              <NumberField help="contrast.quest_beta" ctx={ctx} label="Slope β (per log unit)" value={C.quest_beta} min={1} max={60} step={1}
                onCommit={(v) => set({ quest_beta: v })} />
              <NumberField help="contrast.quest_gamma" ctx={ctx} label="Guess rate γ" value={C.quest_gamma} min={0} max={0.5} step={0.05}
                onCommit={(v) => set({ quest_gamma: v })} />
              <NumberField help="contrast.quest_delta" ctx={ctx} label="Lapse rate δ" value={C.quest_delta} min={0} max={0.2} step={0.01}
                onCommit={(v) => set({ quest_delta: v })} />
              <NumberField help="contrast.quest_grain" ctx={ctx} label="Grid step (log units)" value={C.quest_grain} min={0.001} max={0.1} step={0.005}
                onCommit={(v) => set({ quest_grain: v })} />
            </>
          )}
        </div>
      </details>
      <TimingFields t={C.timing} ctx={ctx} onChange={(t) => set({ timing: t })} />
    </>
  );
}
